import React from "react";
import type { SortDir, SortKey } from "../../../types";
import type { StatusTone } from "../../../utils/user";

export const ACCENT = "#c06198";

export const StatusDot = ({ tone }: { tone: StatusTone }) => {
    const cls =
        tone === "ok"
            ? "bg-emerald-500 shadow-[0_0_0_5px_rgba(16,185,129,0.10)]"
            : tone === "warn"
                ? "bg-amber-500 shadow-[0_0_0_5px_rgba(245,158,11,0.12)]"
                : tone === "danger"
                    ? "bg-rose-500 shadow-[0_0_0_5px_rgba(244,63,94,0.12)]"
                    : "bg-slate-300 shadow-[0_0_0_5px_rgba(148,163,184,0.10)]";

    return <span className={`inline-block h-3 w-3 rounded-full ${cls}`} />;
};

export const SortArrows = ({ active, dir }: { active: boolean; dir: SortDir }) => {
    const upActive = active && dir === "asc";
    const downActive = active && dir === "desc";

    return (
        <span className="ml-1.5 inline-flex flex-col leading-none">
      <svg viewBox="0 0 24 24" className={["h-3 w-3", upActive ? "text-slate-700" : "text-slate-300"].join(" ")}>
        <path d="M7 14l5-6 5 6" fill="currentColor" />
      </svg>
      <svg viewBox="0 0 24 24" className={["h-3 w-3 -mt-0.5", downActive ? "text-slate-700" : "text-slate-300"].join(" ")}>
        <path d="M7 10l5 6 5-6" fill="currentColor" />
      </svg>
    </span>
    );
};

export const SortableTh = ({
                               label,
                               colKey,
                               sortKey,
                               sortDir,
                               onClick,
                               align = "left",
                           }: {
    label: string;
    colKey: SortKey;
    sortKey: SortKey;
    sortDir: SortDir;
    onClick: () => void;
    align?: "left" | "center";
}) => {
    const active = sortKey === colKey;

    return (
        <th className={["px-3 py-2.5 font-medium bg-slate-50/60", align === "center" ? "text-center" : "text-left"].join(" ")}>
            <button
                type="button"
                onClick={onClick}
                className={[
                    "inline-flex items-center rounded-lg px-2 py-1 text-xs transition",
                    "hover:bg-slate-100 hover:text-slate-700",
                    active ? "text-slate-800" : "text-slate-500",
                ].join(" ")}
                style={active ? { boxShadow: `0 0 0 1px rgba(192,97,152,0.12) inset` } : undefined}
                title="Сортировать"
            >
                {label}
                <SortArrows active={active} dir={sortDir} />
            </button>
        </th>
    );
};

export const IconBtn = ({
                            title,
                            onClick,
                            disabled,
                            tone = "default",
                            children,
                        }: {
    title: string;
    onClick?: () => void;
    disabled?: boolean;
    tone?: "default" | "accent" | "danger";
    children: React.ReactNode;
}) => {
    const base =
        "inline-flex h-8 w-8 items-center justify-center rounded-2xl border " +
        "transition active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed";

    const cls =
        tone === "danger"
            ? "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
            : tone === "accent"
                ? "border-[rgba(192,97,152,0.30)] bg-[rgba(192,97,152,0.10)] text-[#c06198] hover:bg-[rgba(192,97,152,0.16)]"
                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50";

    return (
        <button type="button" title={title} onClick={onClick} disabled={disabled || !onClick} className={`${base} ${cls}`}>
            {children}
        </button>
    );
};

export const PlanPill = ({ value }: { value: string }) => {
    if (!value) return <span className="text-slate-400">—</span>;

    return (
        <span
            className={[
                "inline-flex max-w-full items-center",
                "rounded-2xl border border-slate-200 bg-slate-50",
                "px-2.5 py-1 text-[12px] font-semibold text-slate-700",
                "whitespace-normal break-words leading-4",
            ].join(" ")}
        >
      {value}
    </span>
    );
};

// icons
export const PencilIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path
            d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0 0-3L16.5 4.5a2.1 2.1 0 0 0-3 0L3 15v5Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
        />
    </svg>
);

export const KeyIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path
            d="M7.5 14a4.5 4.5 0 1 1 3.96-6.6L21 7v4h-3v3h-3v3h-3.2A4.48 4.48 0 0 1 7.5 14Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
        />
    </svg>
);

export const RefreshIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M20 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CreditCardIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path d="M3 7h18v10H3V7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export const TrashIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
        <path d="M6 7h12l-1 14H7L6 7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M9 7V4h6v3" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
);
